import { useCallback, useEffect, useRef, useState } from 'react';
import type { PersonalizedPromptSurface } from '@bower/shared';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { nextPromptGeneration } from '../../utils/personalizedPromptSelection';

export interface PersonalizedPromptGeneration {
  readonly generation: number;
  readonly isReady: boolean;
  readonly advance: () => void;
}

/**
 * Own the per-session rotation counter for every server-selected prompt
 * surface. Waiting for the durable database user id avoids an anonymous/Firebase
 * key being fetched and measured before authentication finishes settling.
 */
export function usePersonalizedPromptGeneration(
  surface: Exclude<PersonalizedPromptSurface, 'onboarding'>,
  internalUserId: string | null | undefined,
): PersonalizedPromptGeneration {
  const workspaceId = useWorkspaceId();
  const rotationKey =
    workspaceId && internalUserId
      ? `personalizedPromptGeneration:${surface}:${workspaceId}:${internalUserId}`
      : null;
  const [generation, setGeneration] = useState(0);
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  const generationRef = useRef(0);
  const claimedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rotationKey) {
      setResolvedKey(null);
      return;
    }
    if (claimedKeyRef.current === rotationKey) return;

    // Claim synchronously: StrictMode may replay the effect before state
    // commits, while this ref still preserves the once-per-identity advance.
    claimedKeyRef.current = rotationKey;
    let next = 0;
    try {
      next = nextPromptGeneration(window.sessionStorage, rotationKey);
    } catch {
      // Private-browser storage failures still have an in-memory rotation.
      next = 0;
    }
    generationRef.current = next;
    setGeneration(next);
    setResolvedKey(rotationKey);
  }, [rotationKey]);

  const advance = useCallback(() => {
    if (!rotationKey || resolvedKey !== rotationKey) return;

    const next = generationRef.current + 1;
    generationRef.current = next;
    try {
      window.sessionStorage.setItem(rotationKey, String(next));
    } catch {
      // Keep the current page rotating when session storage is unavailable.
    }
    setGeneration(next);
  }, [resolvedKey, rotationKey]);

  return {
    generation,
    isReady: rotationKey !== null && resolvedKey === rotationKey,
    advance,
  };
}
